export interface CurrencyInfo {
  code: string;
  displayPrefix: string;
  numberFormat: string;
  debitNumberFormat: string;
  creditNumberFormat: string;
}

const DEFAULT_CURRENCY: CurrencyInfo = {
  code: 'USD',
  displayPrefix: '$',
  numberFormat: '$#,##0.00',
  debitNumberFormat: '[Red]$#,##0.00',
  creditNumberFormat: '[Green]$#,##0.00',
};

const CURRENCY_ALIASES: Array<{ code: string; displayPrefix: string; patterns: RegExp[] }> = [
  { code: 'MYR', displayPrefix: 'MYR ', patterns: [/\bMYR\b/i, /\bRM\s*(?=\d|\b)/i] },
  { code: 'USD', displayPrefix: '$', patterns: [/\bUSD\b/i, /\bUS\$|\$/i] },
  { code: 'SGD', displayPrefix: 'SGD ', patterns: [/\bSGD\b/i, /\bS\$/i] },
  { code: 'EUR', displayPrefix: 'EUR ', patterns: [/\bEUR\b/i, /€/i] },
  { code: 'GBP', displayPrefix: 'GBP ', patterns: [/\bGBP\b/i, /£/i] },
  { code: 'AUD', displayPrefix: 'AUD ', patterns: [/\bAUD\b/i, /\bA\$/i] },
  { code: 'CAD', displayPrefix: 'CAD ', patterns: [/\bCAD\b/i, /\bC\$/i] },
  { code: 'JPY', displayPrefix: 'JPY ', patterns: [/\bJPY\b/i, /¥/i] },
  { code: 'CNY', displayPrefix: 'CNY ', patterns: [/\bCNY\b/i, /\bRMB\b/i] },
  { code: 'HKD', displayPrefix: 'HKD ', patterns: [/\bHKD\b/i, /\bHK\$/i] },
  { code: 'IDR', displayPrefix: 'IDR ', patterns: [/\bIDR\b/i, /\bRp\b/i] },
  { code: 'THB', displayPrefix: 'THB ', patterns: [/\bTHB\b/i, /฿/i] },
  { code: 'PHP', displayPrefix: 'PHP ', patterns: [/\bPHP\b/i, /₱/i] },
  { code: 'INR', displayPrefix: 'INR ', patterns: [/\bINR\b/i, /₹/i] },
];

export function detectCurrencyFromText(text: string): CurrencyInfo {
  const scores = CURRENCY_ALIASES.map((currency) => ({
    ...currency,
    score: currency.patterns.reduce((total, pattern) => total + (text.match(pattern)?.length ?? 0), 0),
  }))
    .filter((currency) => currency.score > 0)
    .sort((a, b) => b.score - a.score);

  const match = scores[0];
  if (!match) return DEFAULT_CURRENCY;

  return {
    code: match.code,
    displayPrefix: match.displayPrefix,
    numberFormat: createCurrencyNumberFormat(match.displayPrefix),
    debitNumberFormat: createCurrencyNumberFormat(match.displayPrefix, 'Red'),
    creditNumberFormat: createCurrencyNumberFormat(match.displayPrefix, 'Green'),
  };
}

export function detectCurrencyFromPdfPages(pages: Array<{ items: unknown[] }>): CurrencyInfo {
  return detectCurrencyFromText(
    pages
      .flatMap((page) => page.items)
      .map((item) => {
        const textItem = item as { str?: unknown; text?: unknown };
        return typeof textItem.str === 'string'
          ? textItem.str
          : typeof textItem.text === 'string'
            ? textItem.text
            : '';
      })
      .join(' '),
  );
}

export function formatCurrencyAmount(value: number, currency: CurrencyInfo): string {
  return `${currency.displayPrefix}${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function createCurrencyNumberFormat(displayPrefix: string, color?: 'Red' | 'Green'): string {
  const colorPrefix = color ? `[${color}]` : '';

  if (/^[^\w\s]+$/.test(displayPrefix)) {
    return `${colorPrefix}${displayPrefix}#,##0.00`;
  }

  return `${colorPrefix}"${displayPrefix.trim()} "#,##0.00`;
}
